import React, { useState, useEffect } from "react";
import { useQuery, useLazyQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";

import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import ReceiptIcon from "@material-ui/icons/Receipt";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import FileCopyIcon from "@material-ui/icons/FileCopy";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import PaymentDetails from "superAdminViews/finance/pay/PaymentDetails";
import ShopTransactionDetails from "./ShopTransactionDetails";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import NumberRangeFilter from "designV2/components/NumberRangeFilter";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import "./finance.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * Two bugs fixed here that silently did nothing on the pre-redesign
 * screen: the "Type" filter dropdown existed but the query never
 * declared a $type variable, and the amount sort arrows updated local
 * state but the query never declared $amountSort either. Both fields
 * are genuinely supported by GetShopPaymentsSortInput / the payments
 * filter input on the backend — this was a wiring gap, not a missing
 * backend feature.
 * ------------------------------------------------------------------ */

const PAYMENT_FIELDS = `
  _id
  transactionId {
    _id
    transactionId
    status
    paidAt
    type
    amount
    reversed
    reversalId
    refundId
    payments {
      _id
      trip {
        _id
      }
      order {
        _id
      }
      status
      for
      description
      createdAt
      updatedAt
      amount
      type
      shop {
        _id
        name
      }
      driver {
        _id
        fullName
      }
    }
    user {
      _id
    }
    driver {
      _id
    }
    shop {
      _id
      name
    }
    paymentIntent
    createdAt
    updatedAt
  }
  trip {
    _id
  }
  order {
    _id
  }
  status
  for
  description
  createdAt
  amount
  type
`;

const GET_PAYMENTS = gql`
  query getShopPayments(
    $limit: Int
    $skip: Int
    $transactionId: String
    $status: PaymentStatus
    $type: PaymentType
    $for: PaymentFor
    $amount: Float
    $amountFrom: Float
    $createdAt: Date
    $createdAtFrom: Date
    $createdAtSort: Int
    $amountSort: Int
  ) {
    getShopPayments(
      pagination: { limit: $limit, skip: $skip }
      filters: {
        transactionId: $transactionId
        status: $status
        type: $type
        for: $for
        amount: $amount
        amountFrom: $amountFrom
        createdAt: $createdAt
        createdAtFrom: $createdAtFrom
      }
      sort: { createdAt: $createdAtSort, amount: $amountSort }
    ) {
      ${PAYMENT_FIELDS}
    }
    getShopPaymentsCount(
      filters: {
        transactionId: $transactionId
        status: $status
        type: $type
        for: $for
        amount: $amount
        amountFrom: $amountFrom
        createdAt: $createdAt
        createdAtFrom: $createdAtFrom
      }
    )
  }
`;

// Lightweight count-only queries per status, so the metric strip shows real
// totals across every payment instead of just summing whatever page of 10
// happens to be on screen right now.
const GET_STATUS_COUNTS = gql`
  query getShopPaymentsStatusCounts {
    paid: getShopPaymentsCount(filters: { status: PAID })
    unpaid: getShopPaymentsCount(filters: { status: UNPAID })
    failed: getShopPaymentsCount(filters: { status: FAILED })
    failedDueToRejection: getShopPaymentsCount(filters: { status: FAILED_DUE_TO_REJECTION })
  }
`;

const STATUS_OPTIONS = [
  { value: "PAID", label: "Paid" },
  { value: "UNPAID", label: "Unpaid" },
  { value: "FAILED", label: "Failed" },
  { value: "FAILED_DUE_TO_REJECTION", label: "Failed (rejection)" },
];
const STATUS_LABELS = STATUS_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});
const STATUS_TONES = {
  PAID: "success",
  UNPAID: "warning",
  FAILED: "danger",
  FAILED_DUE_TO_REJECTION: "danger",
};

const TYPE_OPTIONS = [
  { value: "PAY_TO_SPARK", label: "Pay to Spark" },
  { value: "PAY_TO_SHOP", label: "Pay to shop" },
  { value: "PAY_TO_DRIVER", label: "Pay to driver" },
  { value: "REJECTION_PENALTY", label: "Rejection penalty" },
];
const TYPE_LABELS = TYPE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const FOR_OPTIONS = [
  { value: "DELIVERY", label: "Delivery" },
  { value: "RESTAURANT", label: "Restaurant" },
  { value: "PURCHASE", label: "Purchase" },
  { value: "RIDE", label: "Ride" },
];
const FOR_LABELS = FOR_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "amountHigh", label: "Amount high-low" },
  { value: "amountLow", label: "Amount low-high" },
];

const FILTER_DEBOUNCE_MS = 350;

const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "");
const formatTime = (value) => (value ? moment(value).format("hh:mm A") : "");

const Payments = () => {
  const [transactionRefFilter, setTransactionRefFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [forFilter, setForFilter] = useState("");
  const [amountFrom, setAmountFrom] = useState("");
  const [amountTo, setAmountTo] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [paymentDetail, setPaymentDetail] = useState(null);
  const [transactionDetail, setTransactionDetail] = useState(null);
  const [copiedId, setCopiedId] = useState("");
  const [exporting, setExporting] = useState(false);

  const [variables, setVariables] = useState({
    limit: perPage,
    skip: 0,
    createdAtSort: -1,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setVariables((v) => ({
        ...v,
        skip: 0,
        transactionId: transactionRefFilter.trim() || undefined,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        for: forFilter || undefined,
        amountFrom: amountFrom ? parseFloat(amountFrom) : undefined,
        amount: amountTo ? parseFloat(amountTo) : undefined,
        createdAtFrom: dateFrom || undefined,
        createdAt: dateTo || undefined,
        createdAtSort: sortOption === "oldest" ? 1 : sortOption === "newest" ? -1 : undefined,
        amountSort: sortOption === "amountHigh" ? -1 : sortOption === "amountLow" ? 1 : undefined,
      }));
      setPageCount(0);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [transactionRefFilter, statusFilter, typeFilter, forFilter, amountFrom, amountTo, dateFrom, dateTo, sortOption]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_PAYMENTS, {
    variables,
    fetchPolicy: "network-only",
  });

  const { data: statusCountsData } = useQuery(GET_STATUS_COUNTS, { fetchPolicy: "network-only" });

  const [fetchAllForExport, { data: exportData }] = useLazyQuery(GET_PAYMENTS, { fetchPolicy: "network-only" });

  const payments = (data && data.getShopPayments) || [];
  const totalCount = (data && data.getShopPaymentsCount) || 0;
  const pageAmount = payments.reduce((sum, p) => sum + (p.amount || 0), 0);

  const counts = statusCountsData || {};
  const failedCount = (counts.failed || 0) + (counts.failedDueToRejection || 0);

  /* ---------------- filter chips ---------------- */

  const activeFilterChips = [];
  if (transactionRefFilter.trim()) {
    activeFilterChips.push({ id: "ref", label: `Transaction ref: ${transactionRefFilter}` });
  }
  if (statusFilter) activeFilterChips.push({ id: "status", label: `Status: ${STATUS_LABELS[statusFilter]}` });
  if (typeFilter) activeFilterChips.push({ id: "type", label: `Type: ${TYPE_LABELS[typeFilter]}` });
  if (forFilter) activeFilterChips.push({ id: "for", label: `For: ${FOR_LABELS[forFilter]}` });
  if (amountFrom || amountTo) {
    activeFilterChips.push({ id: "amount", label: `Amount: $${amountFrom || "0"} to $${amountTo || "-"}` });
  }
  if (dateFrom || dateTo) {
    activeFilterChips.push({ id: "date", label: `Created: ${dateFrom || "-"} to ${dateTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "ref") setTransactionRefFilter("");
    if (id === "status") setStatusFilter("");
    if (id === "type") setTypeFilter("");
    if (id === "for") setForFilter("");
    if (id === "amount") {
      setAmountFrom("");
      setAmountTo("");
    }
    if (id === "date") {
      setDateFrom("");
      setDateTo("");
    }
  };

  const clearAllFilters = () => {
    setTransactionRefFilter("");
    setStatusFilter("");
    setTypeFilter("");
    setForFilter("");
    setAmountFrom("");
    setAmountTo("");
    setDateFrom("");
    setDateTo("");
  };

  /* ---------------- export ---------------- */

  useEffect(() => {
    if (!exporting || !exportData) return;
    const rows = [["Transaction ref", "Type", "For", "Status", "Amount", "Created"]];
    exportData.getShopPayments.forEach((p) => {
      rows.push([
        (p.transactionId && p.transactionId.transactionId) || "-",
        TYPE_LABELS[p.type] || p.type || "-",
        FOR_LABELS[p.for] || p.for || "-",
        STATUS_LABELS[p.status] || p.status,
        Number(p.amount || 0).toFixed(2),
        moment(p.createdAt).format("MM/DD/YYYY HH:mm"),
      ]);
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Payments");
    XLSX.writeFile(wb, "Payments.xlsx");
    setExporting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handleExport = () => {
    if (totalCount === 0 || exporting) return;
    setExporting(true);
    fetchAllForExport({ variables: { ...variables, skip: 0, limit: totalCount } });
  };

  const handleCopy = (id) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(id).then(() => {
      setCopiedId(id);
      window.setTimeout(() => setCopiedId(""), 1500);
    });
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page financePage paymentsPage">
        <main className="main">
          <PageHeader title="Payments" description="Every payment recorded against your shop." />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={6} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  return (
    <div className="page financePage paymentsPage">
      <main className="main">
        <PageHeader
          title="Payments"
          count={totalCount}
          description="Every payment recorded against your shop, and which transaction it belongs to."
          actions={
            <PageActionButton
              icon={<CloudDownloadIcon fontSize="small" />}
              label={exporting ? "Exporting" : "Export"}
              info={exporting ? "Preparing file" : `${totalCount} payment${totalCount === 1 ? "" : "s"}`}
              onClick={handleExport}
              disabled={totalCount === 0}
              loading={exporting}
            />
          }
        />

        <section className="stats">
          <MetricCard icon={<AccountBalanceWalletIcon />} title="Total payments" value={totalCount} />
          <MetricCard icon={<CheckCircleOutlineIcon />} title="Paid" value={counts.paid || 0} tone="success" />
          <MetricCard icon={<ErrorOutlineIcon />} title="Unpaid" value={counts.unpaid || 0} tone="warning" />
          <MetricCard
            icon={<ErrorOutlineIcon />}
            title="Failed"
            value={failedCount}
            tone={failedCount > 0 ? "danger" : "default"}
          />
        </section>

        <section className="filters">
          <SearchField
            placeholder="Search by transaction reference..."
            value={transactionRefFilter}
            onChange={(e) => setTransactionRefFilter(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={statusFilter ? `Status: ${STATUS_LABELS[statusFilter]}` : "Status"}
              options={[{ value: "", label: "All statuses" }, ...STATUS_OPTIONS]}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
            <FilterDropdown
              label={typeFilter ? `Type: ${TYPE_LABELS[typeFilter]}` : "Type"}
              options={[{ value: "", label: "All types" }, ...TYPE_OPTIONS]}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            />
            <FilterDropdown
              label={forFilter ? `For: ${FOR_LABELS[forFilter]}` : "For"}
              options={[{ value: "", label: "Anything" }, ...FOR_OPTIONS]}
              value={forFilter}
              onChange={(e) => setForFilter(e.target.value)}
            />
            <NumberRangeFilter
              label="Amount"
              fromValue={amountFrom}
              toValue={amountTo}
              onFromChange={(e) => setAmountFrom(e.target.value)}
              onToChange={(e) => setAmountTo(e.target.value)}
            />
            <DateRangeFilter
              label="Created"
              fromValue={dateFrom}
              toValue={dateTo}
              onFromChange={(e) => setDateFrom(e.target.value)}
              onToChange={(e) => setDateTo(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList filters={activeFilterChips} onClearFilter={clearFilter} onClearAll={clearAllFilters} />
        )}

        <div className="tableTopBar">
          <div className="tableTopBarLeft">
            {payments.length > 0 && (
              <span className="financePageAmount">This page: ${pageAmount.toFixed(2)}</span>
            )}
          </div>
          <div className="tableTopBarRight">
            <IconButton className="tableTopIconButton" size="small" onClick={() => refetch()} title="Refresh payments">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="financeRefetchSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell">Transaction</div>
              <div className="tableHeaderCell">Type / for</div>
              <div className="tableHeaderCell">Status</div>
              <div className="tableHeaderCell">Amount</div>
              <div className="tableHeaderCell">Created</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {payments.length === 0 ? (
              <EmptyState
                icon={<AccountBalanceWalletIcon />}
                title={totalCount === 0 && activeFilterChips.length === 0 ? "No payments yet" : "No matches"}
                message={
                  totalCount === 0 && activeFilterChips.length === 0
                    ? "Payments will show up here once orders start coming in."
                    : "No payments match these filters. Try widening or clearing them."
                }
              />
            ) : (
              payments.map((payment) => {
                const ref = payment.transactionId && payment.transactionId.transactionId;
                return (
                  <div className="row" key={payment._id}>
                    <div className="financeRefCell" data-label="Transaction">
                      {ref ? (
                        <React.Fragment>
                          <span className="financeRef">{ref}</span>
                          <IconButton size="small" className="rowActionButton" onClick={() => handleCopy(ref)} title="Copy">
                            {copiedId === ref ? <CheckCircleOutlineIcon fontSize="small" /> : <FileCopyIcon style={{ fontSize: 16 }} />}
                          </IconButton>
                        </React.Fragment>
                      ) : (
                        <span className="financeRefMuted">No transaction</span>
                      )}
                    </div>

                    <div className="financeTypeCell" data-label="Type / for">
                      <span>{TYPE_LABELS[payment.type] || payment.type || "-"}</span>
                      {payment.for && <span className="financeSubtext">{FOR_LABELS[payment.for] || payment.for}</span>}
                    </div>

                    <span data-label="Status">
                      <StatusBadge status={STATUS_TONES[payment.status] || "neutral"} label={STATUS_LABELS[payment.status] || payment.status} />
                    </span>

                    <span className="financeAmount" data-label="Amount">
                      ${Math.abs(payment.amount || 0).toFixed(2)}
                    </span>

                    <span className="updated" data-label="Created">
                      <strong>{formatDate(payment.createdAt)}</strong>
                      <span>{formatTime(payment.createdAt)}</span>
                    </span>

                    <div className="actions">
                      {payment.transactionId && (
                        <IconButton
                          size="small"
                          className="rowActionButton"
                          onClick={() => setTransactionDetail(payment.transactionId)}
                          title="View transaction"
                        >
                          <ReceiptIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setPaymentDetail(payment)}
                        title="View payment"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {totalCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell open={Boolean(paymentDetail)} onClose={() => setPaymentDetail(null)} title="Payment details">
        {paymentDetail && (
          <PaymentDetails
            close={() => setPaymentDetail(null)}
            refetch={() => refetch()}
            payment={paymentDetail}
            hasResolver={false}
          />
        )}
      </DialogShell>

      <DialogShell
        open={Boolean(transactionDetail)}
        onClose={() => setTransactionDetail(null)}
        title="Transaction details"
      >
        {transactionDetail && <ShopTransactionDetails transaction={transactionDetail} />}
      </DialogShell>
    </div>
  );
};

export default Payments;
